import { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { cn } from '@/utils/cn';

interface AuthShellProps {
  title: string;
  subtitle: string;
  eyebrow?: string;
  footer?: ReactNode;
  alternateAction?: {
    href: string;
    label: string;
  };
  layout?: 'compact' | 'wide';
  mode?: 'split' | 'form';
  children: ReactNode;
}

export const AuthShell = ({
  title,
  subtitle,
  eyebrow,
  footer,
  alternateAction,
  layout = 'compact',
  mode = 'split',
  children,
}: AuthShellProps) => {
  const isWide = layout === 'wide';
  const isFormOnly = mode === 'form';

  return (
    <section className="auth-shell relative min-h-[calc(100dvh-4rem)] bg-[linear-gradient(180deg,_#f8fafc_0%,_#ffffff_52%,_#f8fafc_100%)]">
      {alternateAction && (
        <Link
          to={alternateAction.href}
          className="absolute right-4 top-4 z-10 rounded-lg border border-slate-200 bg-white/95 px-3 py-2 text-sm font-semibold text-[rgb(var(--accent-solid))] shadow-sm shadow-slate-900/5 transition hover:border-slate-300 hover:bg-white hover:text-slate-950 focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-[rgb(var(--accent-solid))/0.16] sm:right-6 lg:right-8"
        >
          {alternateAction.label}
        </Link>
      )}

      <div className="mx-auto flex min-h-[calc(100dvh-4rem)] w-full items-center px-4 py-20 sm:px-6 lg:px-8">
        <div
          className={cn(
            'mx-auto grid w-full items-center gap-8',
            isFormOnly
              ? isWide
                ? 'max-w-5xl'
                : 'max-w-xl'
              : isWide
                ? 'max-w-6xl lg:grid-cols-[minmax(0,0.68fr)_minmax(0,1fr)]'
                : 'max-w-5xl lg:grid-cols-[minmax(0,0.8fr)_minmax(0,0.9fr)]'
          )}
        >
          {!isFormOnly && (
            <div className="flex flex-col justify-center">
              <div className="flex items-start justify-between gap-4 lg:block">
                <div>
                  {eyebrow && <p className="text-sm font-medium uppercase tracking-[0.22em] text-gray-500">{eyebrow}</p>}
                  <h1 className={cn('text-3xl font-semibold tracking-tight text-gray-950 sm:text-4xl', eyebrow && 'mt-3')}>
                    {title}
                  </h1>
                  <p className="mt-3 max-w-xl text-sm leading-6 text-gray-600 sm:text-base">{subtitle}</p>
                </div>
              </div>
            </div>
          )}

          <div className={cn('w-full', !isFormOnly && (isWide ? 'lg:pl-8' : 'lg:pl-10'))}>
            <div className="auth-panel rounded-lg border border-slate-200 bg-white p-5 shadow-xl shadow-slate-900/10 sm:p-6 lg:p-8">
              {isFormOnly && (
                <div className="mb-6">
                  {eyebrow && <p className="auth-eyebrow text-sm font-semibold uppercase tracking-[0.2em] text-slate-500">{eyebrow}</p>}
                  <h1 className={cn('auth-title text-2xl font-semibold tracking-tight text-slate-950 sm:text-3xl', eyebrow && 'mt-3')}>
                    {title}
                  </h1>
                  <p className="auth-subtitle mt-2 max-w-3xl text-sm leading-6 text-slate-600">{subtitle}</p>
                </div>
              )}

              {children}

              {footer && <div className="mt-6 border-t border-slate-200 pt-4 text-sm text-slate-700">{footer}</div>}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
